import type { Principle } from '../types/content';

export const mission =
  'To connect organizations with the leaders who will shape their next chapter — through rigorous, discreet, and deeply considered executive search.';

export const vision =
  'To be the partner boards and chief executives call first when a leadership decision carries lasting consequence.';

export const values: Principle[] = [
  {
    title: 'Discretion',
    description:
      'Every mandate and every conversation is handled with absolute confidentiality, for clients and candidates alike.',
  },
  {
    title: 'Rigor',
    description:
      'We test assumptions, evidence our recommendations, and never present a candidate we would not stand behind.',
  },
  {
    title: 'Candor',
    description:
      'Honest counsel on markets, compensation, and fit — even when it is not what a client or candidate expects to hear.',
  },
  {
    title: 'Stewardship',
    description:
      'We measure success by how leaders perform years after appointment, not by the speed of a placement.',
  },
];

export const searchPhilosophy: string[] = [
  'Great appointments rarely come from the obvious shortlist. We begin every search by understanding the organization’s strategy, culture, and the specific inflection point the role must address.',
  'From there we map the full market — including leaders who are not looking — and assess each person against a clear, agreed definition of success rather than a generic job description.',
  'The result is a small number of exceptional, genuinely interested candidates, each presented with candid analysis of their strengths, risks, and likely impact.',
];

export const clientPartnership: Principle[] = [
  {
    title: 'A defined mandate',
    description: 'We co-author the role brief and success profile with your board or leadership team before outreach begins.',
  },
  {
    title: 'Transparent progress',
    description: 'Regular, structured updates with market feedback, pipeline status, and clear next steps.',
  },
  {
    title: 'Senior attention throughout',
    description: 'Your search is led by experienced partners from first conversation to final offer.',
  },
  {
    title: 'Support beyond the offer',
    description: 'We stay close through transition and the first months in role to help new leaders succeed.',
  },
];

export const candidatePrinciples: Principle[] = [
  {
    title: 'Confidential by default',
    description: 'Your interest is never shared with a client without your explicit consent.',
  },
  {
    title: 'Honest about the opportunity',
    description: 'We share what we know about the role, the organization, and its challenges — not just the highlights.',
  },
  {
    title: 'Respect for your time',
    description: 'Clear timelines, prompt feedback, and a direct answer at every stage, including when the fit is not right.',
  },
  {
    title: 'A long-term relationship',
    description: 'We invest in leaders across their careers, not only when a mandate matches.',
  },
];

export const methodology: Principle[] = [
  {
    title: 'Discovery & mandate',
    description: 'Stakeholder interviews to define the role, its context, and what success looks like at 12 and 36 months.',
  },
  {
    title: 'Market mapping',
    description: 'Research across sectors and geographies to identify the full universe of relevant leaders.',
  },
  {
    title: 'Engagement',
    description: 'Discreet, personal outreach to prospective candidates, including those not actively looking.',
  },
  {
    title: 'Assessment',
    description: 'Structured, competency-based interviews and referencing against the agreed success profile.',
  },
  {
    title: 'Shortlist & selection',
    description: 'A focused shortlist with candid evaluations, followed by facilitated client interviews.',
  },
  {
    title: 'Offer & transition',
    description: 'Negotiation support, onboarding guidance, and check-ins through the first months in role.',
  },
];
